import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, matchPath, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { HierarchyFilterProvider } from '../hooks/useHierarchyFilter';
import { EmptyState } from '../components/shared/States';
import { FilterBar } from './FilterBar';
import { Nav } from './Nav';
import { ShellContext } from './Page';
import { ShellStatusProvider, useShellStatus } from './ShellStatus';
import { Topbar } from './Topbar';
import { HelpProvider } from '../help/HelpContext';
import { HelpPanel } from '../help/HelpPanel';
import '../help/help.css';

/** Redirects to /login when there is no Vertolytics session. The API enforces every permission. */
export function RequireAuth({ children }) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  if (!isAuthenticated) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return children;
}

/** Hides content the signed-in role can't use. Client-side only; it does not replace API authorization. */
export function RequirePermission({ permission, children }) {
  const { hasPermission } = useAuth();
  if (permission && !hasPermission(permission)) {
    return (
      <EmptyState
        icon="lock"
        title="You don't have access to this page"
        body="Your role doesn't include this view. Ask a Vertolytics administrator in your organization if you need it."
      />
    );
  }
  return children;
}

function FilterState({ children }) {
  const { orgName, timeZone, terms } = useShellStatus();
  return (
    <HierarchyFilterProvider orgName={orgName} timeZone={timeZone} terms={terms}>
      {children}
    </HierarchyFilterProvider>
  );
}

function ShellFrame({ hideFiltersOn }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [filterSlot, setFilterSlot] = useState(null);
  const showFilters = !hideFiltersOn.some((pattern) => matchPath({ path: pattern, end: true }, location.pathname));

  useEffect(() => {
    document.documentElement.classList.remove('nav-open');
  }, [location.pathname]);

  return (
    <ShellContext.Provider value={{ filterSlot: showFilters ? filterSlot : null }}>
      <a className="sr-only skip-link" href="#main">
        Skip to content
      </a>
      <div className="app">
        <Nav user={user} onSignOut={logout} />
        <div className="main">
          <Topbar onToggleNav={() => document.documentElement.classList.toggle('nav-open')} />
          {showFilters && <FilterBar ref={setFilterSlot} />}
          <main className="page" id="main">
            <Outlet />
          </main>
        </div>
      </div>
    </ShellContext.Provider>
  );
}

/**
 * Approved app shell (docs/design/README.md, "For the UI shell lane"): navy nav, top bar,
 * filter bar, and the routed page. hideFiltersOn lists route patterns without the filter bar.
 */
export function AppShell({ hideFiltersOn = [] }) {
  return (
    <ShellStatusProvider>
      <FilterState>
        <HelpProvider>
          <ShellFrame hideFiltersOn={hideFiltersOn} />
          <HelpPanel />
        </HelpProvider>
      </FilterState>
    </ShellStatusProvider>
  );
}

/** Full-screen pages without the nav (wallboard, onboarding). They still get shell status and filter state. */
export function BareShell() {
  return (
    <ShellStatusProvider>
      <FilterState>
        <ShellContext.Provider value={{ filterSlot: null }}>
          <Outlet />
        </ShellContext.Provider>
      </FilterState>
    </ShellStatusProvider>
  );
}
